/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { Map, TileLayer } from 'react-leaflet'
import { useSelector } from 'react-redux'

import L from 'leaflet'
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png'
import iconUrl from 'leaflet/dist/images/marker-icon.png'
import shadowUrl from 'leaflet/dist/images/marker-shadow.png'
import PropTypes from 'prop-types'

import groupBy from 'lodash/groupBy'
import isEmpty from 'lodash/isEmpty'
import mapLodash from 'lodash/map'
import toString from 'lodash/toString'

import { useT } from '@smartcoop/i18n'
import { pause, play } from '@smartcoop/icons'
import { getWeatherSeries } from '@smartcoop/services/apis/smartcoopApi/resources/weatherForecast'
import { selectAccessToken } from '@smartcoop/stores/authentication/selectorAuthentication'
import { selectCurrentPropertyGeolocalization } from '@smartcoop/stores/property/selectorProperty'
import { colors } from '@smartcoop/styles'
import { legendDataClouds, legendDataPrecipitation, legendDataRadar, legendDataTemperature, legendWind } from '@smartcoop/utils/maps'
import AnimatedLocation from '@smartcoop/web-components/AnimatedLocation'
import Icon from '@smartcoop/web-components/Icon'
import IconButton from '@smartcoop/web-components/IconButton'
import InputSelect from '@smartcoop/web-components/InputSelect'
import Control from '@smartcoop/web-components/Maps/components/Control'

import { ColorBox, CustomSlider, LegendContainer, LegendItem, LegendTitle, MapContainer } from './styles'

if (typeof window !== 'undefined') {
  delete L.Icon.Default.prototype._getIconUrl
  L.Icon.Default.mergeOptions({
    iconRetinaUrl,
    iconUrl,
    shadowUrl
  })
}

// eslint-disable-next-line react/prop-types
const WeatherLayer = ({ map, setCurrentLayer, currentLayer }) => {

  const t = useT()

  const [radarLayers, setRadarLayers] = useState({})
  const [timestamps, setTimestamps] = useState([])
  const [baseTs, setBaseTs] = useState(null)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const animationInterval = useRef(null)

  const accessToken = useSelector(selectAccessToken)
  const prevLayerRef = useRef(null)
  const prevTimestampsRef = useRef(null)

  const fetchAvailableTimestamps = useCallback(async () => {
    try {
      const { data } = await getWeatherSeries()

      if (data.seriesInfo && data.seriesInfo[currentLayer]) {
        const series = data.seriesInfo[currentLayer].series[0]

        setBaseTs(series.ts)

        const reversedTimestamps = series?.fts?.reverse?.() || []

        setTimestamps([...reversedTimestamps])
        setCurrentFrame(0)

        prevLayerRef.current = currentLayer
        prevTimestampsRef.current = reversedTimestamps
      }
    } catch (error) {
      console.error('Erro ao obter timestamps:', error)
    }
  }, [currentLayer])

  useEffect(() => {
    fetchAvailableTimestamps()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLayer])

  const updateRadarLayer = useCallback(() => {
    if (timestamps.length === 0 || !map || !baseTs) return
    const fts = timestamps[currentFrame]

    Object.values(radarLayers).forEach((layers) => {
      layers.forEach((layer) => {
        if (map.hasLayer(layer)) {
          map.removeLayer(layer)
        }
      })
    })

    setRadarLayers({})

    // Criar a nova camada
    const newLayer = L.tileLayer(
      `${ process.env.REACT_APP_SMARTCOOP_API_URL }v1/weather/tile-layers?type=${ currentLayer }&ts=${ baseTs }${ `&fts=${ timestamps[currentFrame] }` }&xyz={x}:{y}:{z}&token=${ accessToken.split(' ')[1] }`,
      { opacity: 0 }
    )

    // Adicionar ao mapa
    newLayer.addTo(map)

    // 🔴 Atualizar estado com APENAS a nova camada
    setRadarLayers({ [fts]: [newLayer] })
    setTimeout(() => newLayer.setOpacity(0.7), 100)

  }, [timestamps, map, baseTs, currentFrame, radarLayers, currentLayer, accessToken])

  useEffect(() => {
    if (isPlaying) {
      animationInterval.current = setInterval(() => {
        setCurrentFrame((prevFrame) => (prevFrame + 1) % timestamps.length)
      }, 1500)
    } else {
      clearInterval(animationInterval.current)
    }
    return () => clearInterval(animationInterval.current)
  }, [isPlaying, timestamps])

  useEffect(() => {
    updateRadarLayer()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFrame, currentLayer, timestamps])

  const layerOptions = useMemo(() => [
    { label: 'Radar', value: 'satradFcst' },
    { label: 'Temperatura', value: 'tempFcst' },
    { label: 'Vento', value: 'windSpeedFcst' }
  ], [])

  const formatTimestamp = (ts) => {
    const date = new Date(ts * 1000)
    return date.toLocaleString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const maxItems = 8
  const totalItems = timestamps.length

  let timestampsToShow = []
  if (totalItems > 1) {
    timestampsToShow = [timestamps[0]]

    const step = Math.floor((totalItems - 1) / (maxItems - 2))
    // eslint-disable-next-line no-plusplus
    for (let i = 1; i < maxItems - 1; i++) {
      const index = i * step
      if (index < totalItems - 1) {
        timestampsToShow.push(timestamps[index])
      }
    }

    timestampsToShow.push(timestamps[totalItems - 1])
  }

  return (
    <>
      <Control position="topleft">
        <div style={ { display: 'flex', flexDirection: 'row', background: 'white', paddingTop: 10, paddingLeft: 10, paddingRight: 10, borderRadius: 10, width: 250 } }>
          <InputSelect
            label={ t('layer') }
            detached
            options={ layerOptions }
            value={ currentLayer }
            onChange={ (value) => setCurrentLayer(value) }
          />
        </div>
      </Control>
      <Control position="bottomcenter">
        <div style={ { display: 'flex', flexDirection: 'row', background: 'white', paddingTop: 10, paddingLeft: 10, paddingRight: 10, borderRadius: 10 } }>
          <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' } }>
            <IconButton
              onClick={ () => setIsPlaying(!isPlaying) }
            >
              <Icon size={ 16 } icon={ isPlaying ? pause : play } color={ colors?.black } />
            </IconButton>
            <span style={ { fontSize: 12, fontWeight: 'bold' } }>{formatTimestamp(timestamps[currentFrame])}</span>
          </div>
          <div style={ { display: 'flex', flexDirection: 'column', marginTop: 10, paddingBottom: 10, flex: 1 } }>
            <CustomSlider
              type="range"
              min={ 0 }
              max={ timestamps.length - 1 }
              value={ currentFrame }
              onChange={ (e) => setCurrentFrame(Number(e.target.value)) }
              style={ { width: '100%' } }
            />

            {/* Labels abaixo do slider */}
            <div style={ { display: 'flex', justifyContent: 'space-between', marginTop: 5 } }>
              {timestampsToShow.map((ts, index) => (
                <span key={ index } style={ { fontSize: 10 } }>
                  {formatTimestamp(ts)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Control>
    </>
  )
}

const RadarMapLegend = ({ currentLayer }) => (
  <LegendContainer>
    <LegendTitle>Legenda do Mapa</LegendTitle>
    {currentLayer === 'satradFcst' && mapLodash(groupBy(legendDataClouds, 'group'), (group, groupIndex) => (
      <div key={ groupIndex }>
        <LegendTitle>{group[0].group}</LegendTitle>
        {group.map(({ color, value, label }, index) => (
          <LegendItem key={ index }>
            <ColorBox color={ color } />
            <span style={ { fontSize: 10 } }>{value} {label && `- ${ label }`}</span>
          </LegendItem>
        ))}
      </div>
    ))}
    {mapLodash(groupBy(legendDataRadar, 'group'), (group, groupIndex) => (
      <div key={ groupIndex }>
        <LegendTitle>{group[0].group}</LegendTitle>
        {group.map(({ color, value, label }, index) => (
          <LegendItem key={ index }>
            <ColorBox color={ color } />
            <span style={ { fontSize: 10 } }>{value} {label && `- ${ label }`}</span>
          </LegendItem>
        ))}
      </div>
    ))}
  </LegendContainer>
)

const PrecipitationMapLegend = () => (
  <LegendContainer>
    <LegendTitle>Legenda do Mapa</LegendTitle>
    {mapLodash(legendDataPrecipitation, ({ color, value, label }, index) => (
      <div key={ index }>
        <LegendItem key={ index }>
          <ColorBox color={ color } />
          <span style={ { fontSize: 10 } }>{value} {label && `- ${ label }`}</span>
        </LegendItem>
      </div>
    ))}
  </LegendContainer>
)

const TemperatureMapLegend = () => (
  <LegendContainer>
    <LegendTitle>Legenda do Mapa</LegendTitle>
    {mapLodash(legendDataTemperature, ({ color, value, label }, index) => (
      <div key={ index }>
        <LegendItem key={ index }>
          <ColorBox color={ color } />
          <span style={ { fontSize: 10 } }>{value} {label && `- ${ label }`}</span>
        </LegendItem>
      </div>
    ))}
  </LegendContainer>
)

const WindSpeedMapLegend = () => (
  <LegendContainer>
    <LegendTitle>Legenda do Mapa</LegendTitle>
    {mapLodash(legendWind, ({ color, value, label }, index) => (
      <div key={ index }>
        <LegendItem key={ index }>
          <ColorBox color={ color } />
          <span style={ { fontSize: 10 } }>{value} {label && `- ${ label }`}</span>
        </LegendItem>
      </div>
    ))}
  </LegendContainer>
)
const WeatherMapFragment = ({ zoom, onClick, activeTab }) => {
  const mapRef = useRef(null)

  const geolocalization = useSelector(selectCurrentPropertyGeolocalization)

  const [currentLayer, setCurrentLayer] = useState('satradFcst')

  const center = useMemo(() => [-30.0346, -51.2177], [])

  useEffect(() => {
    if (!mapRef.current.leafletElement || isEmpty(toString(geolocalization?.latitude)) ||isEmpty(toString( geolocalization?.longitude))) return

    const pane = mapRef.current.leafletElement.createPane('custom-pane')
    pane.style.zIndex = 650

    // Defina o tamanho do AnimatedLocation (ajuste conforme necessário)
    const iconSize = [50, 50] // Largura e altura do ícone
    const iconAnchor = [50, 50] // Centralizar na coordenada

    const customIcon = L.divIcon({
      html: '<div id="animated-location-container"></div>',
      className: 'custom-icon',
      iconSize,
      iconAnchor // Centraliza o ícone
    })

    const marker = L.marker([geolocalization?.latitude, geolocalization?.longitude], {
      icon: customIcon,
      pane: 'custom-pane'
    }).addTo(mapRef.current.leafletElement)

    setTimeout(() => {
      const container = document.getElementById('animated-location-container')
      if (container) {
        ReactDOM.render(<AnimatedLocation />, container)
      }
    }, 0)

    // eslint-disable-next-line consistent-return
    return () => {
      if (marker) {
        mapRef.current.leafletElement.removeLayer(marker)
      }

      const container = document.getElementById('animated-location-container')
      if (container) {
        ReactDOM.unmountComponentAtNode(container)
      }
    }
  }, [geolocalization, mapRef])

  useEffect(() => {
    if (activeTab === 'weather') {
      mapRef.current.leafletElement.invalidateSize()
    }
  }, [activeTab])

  return (
    <MapContainer key="weather-map-container" id="weather-map-container">
      <Map center={ center } zoom={ zoom } ref={ mapRef } style={ { height: '100%', width: '100%' } } onClick={ onClick }>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Control position="bottomcenter">
          {mapRef.current && <WeatherLayer map={ mapRef.current.leafletElement } setCurrentLayer={ setCurrentLayer } currentLayer={ currentLayer } />}
        </Control>
        <Control position="topright">
          {(mapRef.current && (currentLayer === 'satradFcst' || currentLayer === 'radarFcstV2')) && <RadarMapLegend currentLayer={ currentLayer } map={ mapRef.current.leafletElement } />}
          {(mapRef.current && currentLayer === 'precip1hrCumulativeFcst') && <PrecipitationMapLegend map={ mapRef.current.leafletElement } />}
          {(mapRef.current && currentLayer === 'tempFcst') && <TemperatureMapLegend map={ mapRef.current.leafletElement } />}
          {(mapRef.current && currentLayer === 'windSpeedFcst') && <WindSpeedMapLegend map={ mapRef.current.leafletElement } />}
        </Control>
      </Map>
    </MapContainer>
  )
}

WeatherMapFragment.propTypes = {
  region: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number
  }),
  zoom: PropTypes.number,
  onClick: PropTypes.func
}

WeatherMapFragment.defaultProps = {
  region: { latitude: -30.0346, longitude: -51.2177 },
  zoom: 6,
  onClick: () => {}
}

export default WeatherMapFragment
